
import React from 'react';

const styles = `
  .chs-section {
    background-color: #ffffff;
    background-image: var(--section-bg-gradient);
    padding: 56px max(60px, calc(50% - 660px));
    font-family: Inter, sans-serif;
  }
  .chs-heading {
    font-size: 40px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--color-brand-primary);
    margin: 0 0 8px 0;
  }
  .chs-heading-accent { color: var(--color-brand-green); }
  .chs-desc {
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--color-text-subheading);
    margin: 0;
  }
  .chs-body {
    display: flex;
    align-items: center;
    gap: 40px;
    margin-top: 24px;
  }
  .chs-media {
    width: 55%;
    flex-shrink: 0;
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .chs-media video,
  .chs-media img {
    width: auto;
    max-width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .chs-steps {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .chs-step { display: flex; gap: 14px; }
  .chs-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 36px;
    flex-shrink: 0;
  }
  .chs-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
    flex-shrink: 0;
  }
  .chs-num {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1.5px solid var(--color-brand-primary);
    color: var(--color-brand-primary);
    font-weight: 700;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  /* dotted green connector ending in an arrow head */
  .chs-line {
    flex: 1;
    min-height: 48px;
    width: 0;
    border-left: 2px dotted var(--color-brand-green);
    margin-top: 6px;
  }
  .chs-arrow { margin-bottom: 6px; flex-shrink: 0; }
  .chs-text { padding-bottom: 28px; }
  .chs-step-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--color-brand-primary);
    margin: 0;
  }
  .chs-step-desc {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.35;
    color: #1E1E1E;
    margin: 2px 0 0 0;
  }
  .chs-btn {
    margin-top: 32px;
    background: var(--color-brand-primary);
    color: #fff;
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 15px;
    border: 1.5px solid var(--color-brand-primary);
    border-radius: 999px;
    padding: 10px 28px;
    cursor: pointer;
    transition: background 0.2s;
  }
  .chs-btn:hover { background: var(--color-brand-primary-hover); }

  @media (max-width: 1279px) and (min-width: 1101px) {
    .chs-section { padding: 48px 40px; }
    .chs-heading { font-size: 36px; }
  }
  @media (max-width: 1100px) and (min-width: 768px) {
    .chs-section { padding: 40px 28px; }
    .chs-heading { font-size: 30px; }
    .chs-desc { font-size: 17px; }
    .chs-media { width: 50%; height: 320px; }
    .chs-body { gap: 24px; }
  }
  @media (max-width: 767px) {
    .chs-section { padding: 32px 14px; }
    .chs-heading { font-size: 26px; }
    .chs-desc { font-size: 15px; }
    .chs-body { flex-direction: column; gap: 20px; }
    .chs-media { width: 100%; height: 260px; }
    .chs-step-title { font-size: 16px; }
    .chs-step-desc { font-size: 14px; }
    .chs-text { padding-bottom: 20px; }
    .chs-line { min-height: 36px; }
  }
`;

// "Check Your Lungs Health in Just 4 Steps" → "in Just 4 Steps" in green
// "Care Made Simple with CureBay"          → "Simple with CureBay" in green (CareSathi)
const splitHeading = (heading = "") => {
  const m = heading.match(/^(.*?)\s+(in\s+just\b.*)$/i) || heading.match(/^(.*?)\s+(simple\b.*)$/i);
  return m ? [m[1], m[2]] : [heading, ""];
};

// mediaAlign="start" left-aligns the video/image under the heading (CareSathi's Figma);
// other pages keep the default centred media.
const CheckHealthSteps = ({ data = {}, onButtonClick, mediaAlign }) => {
  const fourthsub = data.fourthsub || [];
  const [headingMain, headingAccent] = splitHeading(data.heading);

  return (
    <>
      <style>{styles}</style>
      <section className="chs-section">

        {/* 🔹 Heading + Description — left aligned */}
        <h2 className="chs-heading">
          {headingMain}{headingAccent && " "}
          {headingAccent && <span className="chs-heading-accent">{headingAccent}</span>}
        </h2>
        {data.desc || data.desc1 ? (
          <p className="chs-desc">{data.desc || data.desc1}</p>
        ) : null}

        {/* 🔹 Two-column: Video left, Steps right */}
        <div className="chs-body">

          {/* Left: Video / Image player */}
          <div className="chs-media" style={mediaAlign === "start" ? { justifyContent: "flex-start" } : undefined}>
            {data.imgurl && (
              data.imgurl.endsWith(".mp4") ? (
                <video src={data.imgurl} loop autoPlay muted controls playsInline />
              ) : (
                <img src={data.imgurl} alt={data.heading} />
              )
            )}
          </div>

          {/* Right: Vertical steps with dotted connector */}
          <div className="chs-steps">
            {fourthsub.map((item, index) => (
              <div key={item.id} className="chs-step">
                <div className="chs-rail">
                  {item.imgurl ? (
                    <img src={item.imgurl} alt={item.subheading} className="chs-icon" />
                  ) : (
                    <span className="chs-num">{index + 1}</span>
                  )}

                  {index < fourthsub.length - 1 && (
                    <>
                      <span className="chs-line" />
                      <svg className="chs-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                        <path d="M1 1l5 5 5-5" stroke="#00BD7F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </div>

                <div className="chs-text">
                  <h3 className="chs-step-title">{item.subheading}</h3>
                  <p className="chs-step-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Optional button */}
        {data.button && (
          <button className="chs-btn" onClick={onButtonClick}>
            {data.button}
          </button>
        )}
      </section>
    </>
  );
};

export default CheckHealthSteps;
